import {
  ReactFramework,
  TemplateStack,
  DesignSystem,
} from "@/utils/templateProjects";
import type { UploadedFile } from "@/utils/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface ProjectCreateStore {
  hasHydrated: boolean;
  setHasHydrated: (state) => void;

  generateText: string;
  generateAttachedFiles: UploadedFile[] | null;
  setGenerateText: (text: string) => void;
  setGenerateAttachedFiles: (files: UploadedFile[] | null) => void;

  // Pending files to upload to codebase once canvas is created
  pendingCodebaseFiles: File[];
  setPendingCodebaseFiles: (files: File[]) => void;
  addPendingCodebaseFiles: (files: File[]) => void;
  clearPendingCodebaseFiles: () => void;

  resetProjectCreateFlow: () => void;

  autoTriggerGenerate: boolean;
  setAutoTriggerGenerate: (state: boolean) => void;

  // Project type
  reactFramework: ReactFramework;
  setReactFramework: (framework: ReactFramework) => void;

  selectedTemplateStack: TemplateStack;
  setSelectedTemplateStack: (stack: TemplateStack | null) => void;

  selectedDesignSystem: DesignSystem | null;
  setSelectedDesignSystem: (designSystem: DesignSystem | null) => void;
}

export const useProjectCreateStore = create<ProjectCreateStore>()(
  persist(
    (set, get) => ({
      hasHydrated: false,
      setHasHydrated: (state) => set({ hasHydrated: state }),

      generateText: "",
      generateAttachedFiles: null,
      setGenerateText: (text: string) => set({ generateText: text }),
      setGenerateAttachedFiles: (files: UploadedFile[] | null) =>
        set({ generateAttachedFiles: files }),

      pendingCodebaseFiles: [],
      setPendingCodebaseFiles: (files: File[]) =>
        set({ pendingCodebaseFiles: files }),
      addPendingCodebaseFiles: (files: File[]) => {
        const currentFiles = get().pendingCodebaseFiles;
        set({ pendingCodebaseFiles: [...currentFiles, ...files] });
      },
      clearPendingCodebaseFiles: () => set({ pendingCodebaseFiles: [] }),

      resetProjectCreateFlow: () => {
        set({
          generateText: "",
          generateAttachedFiles: null,
          pendingCodebaseFiles: [],
          reactFramework: ReactFramework.VITE,
          autoTriggerGenerate: false,
          selectedTemplateStack: TemplateStack.BLANK,
          selectedDesignSystem: DesignSystem.BLANK,
        });
      },

      autoTriggerGenerate: false,
      setAutoTriggerGenerate: (state: boolean) =>
        set({ autoTriggerGenerate: state }),

      reactFramework: ReactFramework.VITE,
      setReactFramework: (framework: ReactFramework) =>
        set({ reactFramework: framework }),

      selectedTemplateStack: TemplateStack.BLANK,
      setSelectedTemplateStack: (stack: TemplateStack) =>
        set({ selectedTemplateStack: stack }),

      selectedDesignSystem: DesignSystem.BLANK,
      setSelectedDesignSystem: (designSystem: DesignSystem) =>
        set({ selectedDesignSystem: designSystem }),
    }),
    {
      name: "project-create-store",
      onRehydrateStorage: (state) => {
        return () => state.setHasHydrated(true);
      },
      // Note: File objects can't be serialized, so we'll exclude pendingCodebaseFiles from persistence
      partialize: (state) => ({
        ...state,
        pendingCodebaseFiles: [], // Don't persist File objects
      }),
    }
  )
);
